/**
 * Persistência de sessão de chat no localStorage.
 * TAREFA 1.5 — Evitar perda de mensagens ao fechar aba ou recarregar.
 */
import { useState, useEffect, useCallback } from "react";

const MAX_MESSAGES = 50;
const MAX_BYTES = 500 * 1024; // ~500KB

export function getStorageKey(sessionId: string): string {
  return `chat-${sessionId}`;
}

/** Remove a conversa persistida do localStorage (ex: ao sair da conversa). */
export function clearPersistedChat(sessionId: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(getStorageKey(sessionId));
  } catch {
    // ignora
  }
}

function truncateToLimit<T>(items: T[], toJson: (x: T[]) => string): T[] {
  if (items.length <= MAX_MESSAGES) {
    const json = toJson(items);
    if (json.length <= MAX_BYTES) return items;
  }
  let trimmed = items.slice(-MAX_MESSAGES);
  while (trimmed.length > 1 && toJson(trimmed).length > MAX_BYTES) {
    trimmed = trimmed.slice(1);
  }
  return trimmed;
}

/**
 * Hook que persiste mensagens no localStorage.
 * API idêntica a useState para facilitar substituição.
 */
export function usePersistedChat<T extends { role: string; content: string }>(
  sessionId: string,
  initialValue: T[] = []
): [T[], React.Dispatch<React.SetStateAction<T[]>>] {
  const key = getStorageKey(sessionId);

  const [messages, setMessagesState] = useState<T[]>(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return initialValue;
      const parsed = JSON.parse(raw) as T[];
      return Array.isArray(parsed) ? parsed : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setMessages = useCallback<React.Dispatch<React.SetStateAction<T[]>>>(
    (action) => {
      setMessagesState((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        const trimmed = truncateToLimit(next, (arr) => JSON.stringify(arr));
        if (typeof window !== "undefined") {
          try {
            const json = JSON.stringify(trimmed);
            if (json.length <= MAX_BYTES) {
              localStorage.setItem(key, json);
            } else {
              const reduced = truncateToLimit(trimmed, (a) => JSON.stringify(a));
              localStorage.setItem(key, JSON.stringify(reduced));
            }
          } catch {
            // quota exceeded ou outro erro — ignora
          }
        }
        return trimmed;
      });
    },
    [key]
  );

  return [messages, setMessages];
}
